import { useEffect, useRef, type ReactNode, type RefObject } from "react";
import cn from "mcn";
import { Button, Checkbox } from "../ui";
import { CloseIcon } from "../icons/CloseIcon";
import { cardContainer, type AnchorPosition } from "../ui/shared-styles";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner } from "@fortawesome/pro-regular-svg-icons";
import { Tooltip, TooltipTrigger } from "@artis/ui";

export function ConfirmDialog({
  open,
  title,
  subtitle,
  message,
  confirmButtonText,
  onCancel,
  onCancelKeepMenu,
  onConfirm,
  variant = "default",
  showCheckbox = false,
  checkboxLabel,
  checkboxChecked = false,
  onCheckboxChange,
  anchorPosition,
  menuRef,
  confirmButtonDisabled,
  confirmButtonTooltip,
  confirmLoading,
  contentClassName,
}: {
  open: boolean;
  title: string;
  /** Sits directly under the title, tighter than the gap to the message body. */
  subtitle?: ReactNode;
  message: ReactNode;
  confirmButtonText?: string;
  onCancel: () => void;
  onCancelKeepMenu?: () => void;
  onConfirm: () => void;
  variant?: "default" | "danger" | "neutral";
  showCheckbox?: boolean;
  checkboxLabel?: string;
  checkboxChecked?: boolean;
  onCheckboxChange?: (checked: boolean) => void;
  anchorPosition?: AnchorPosition;
  menuRef?: RefObject<HTMLDivElement>;
  confirmButtonDisabled?: boolean;
  confirmButtonTooltip?: string;
  confirmLoading?: boolean;
  /** Merged into the dialog panel (e.g. wider max-width for complex forms). */
  contentClassName?: string;
}) {
  const previousActiveElement = useRef<HTMLElement | null>(null);
  const dialogRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (open) {
      previousActiveElement.current = document.activeElement as HTMLElement;
    } else if (previousActiveElement.current) {
      previousActiveElement.current.focus();
      previousActiveElement.current = null;
    }
  }, [open]);

  useEffect(() => {
    if (!open || !anchorPosition) return;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;

      if (dialogRef.current?.contains(target)) {
        return;
      }

      if (menuRef?.current?.contains(target)) {
        onCancelKeepMenu?.();
        return;
      }

      onCancel();
    };

    const timer = setTimeout(() => {
      document.addEventListener("mousedown", handleClickOutside);
    }, 0);

    return () => {
      clearTimeout(timer);
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [open, anchorPosition, onCancel, onCancelKeepMenu, menuRef]);

  useEffect(() => {
    if (!open) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Tab") {
        e.stopPropagation();
        return;
      }

      if (e.key === "Escape") {
        e.preventDefault();
        e.stopPropagation();
        onCancel();
        return;
      }

      // Don't swallow keystrokes that are being typed into editable elements
      // inside the dialog (e.g. Curve name / Display name inputs in Save
      // Curve). Without this, the catch-all preventDefault below blocks all
      // text input.
      const activeElement = document.activeElement as HTMLElement | null;
      const isEditable =
        activeElement &&
        (activeElement.tagName === "INPUT" || activeElement.tagName === "TEXTAREA" || activeElement.isContentEditable);
      if (isEditable && dialogRef.current?.contains(activeElement)) {
        e.stopPropagation();
        return;
      }

      if (e.key === "Enter") {
        if (activeElement?.tagName === "BUTTON" && dialogRef.current?.contains(activeElement)) {
          e.stopPropagation();
          return;
        }
        e.preventDefault();
        e.stopPropagation();
        return;
      }

      e.preventDefault();
      e.stopPropagation();
    };

    document.addEventListener("keydown", handleKeyDown, true);
    return () => document.removeEventListener("keydown", handleKeyDown, true);
  }, [open, onCancel]);

  if (!open) return null;

  const confirmButtonType = variant === "neutral" ? "closePrimary" : variant === "danger" ? "deletePrimary" : "save";

  const dialogContent = (
    <div
      ref={dialogRef}
      className={cn(
        "text-[var(--foreground-default)] p-5 flex flex-col gap-4 w-[calc(100vw-32px)] max-w-[460px] min-w-0 select-none [&_input]:select-text [&_textarea]:select-text",
        contentClassName,
        cardContainer,
        anchorPosition && "pointer-events-auto",
      )}
      style={
        anchorPosition
          ? {
              position: "fixed",
              bottom: anchorPosition.bottom,
              left: anchorPosition.left,
              maxHeight: `calc(100vh - ${anchorPosition.bottom}px - 16px)`,
              overflowY: "auto",
              zIndex: 999999,
            }
          : undefined
      }
      onClick={(e) => e.stopPropagation()}
      role="alertdialog"
      data-testid="confirm-dialog"
    >
      <div className="flex flex-col gap-[4px]">
        <div className="flex items-center justify-between gap-2">
          <div className="text-lg font-semibold break-words min-w-0">{title}</div>
          {anchorPosition && <CloseIcon onClick={onCancel} />}
        </div>
        {subtitle && <div className="min-w-0 whitespace-normal">{subtitle}</div>}
      </div>

      <div className="min-w-0 whitespace-normal">{message}</div>

      <div
        className={cn(
          "flex items-center gap-4 whitespace-nowrap flex-wrap",
          showCheckbox && checkboxLabel && onCheckboxChange ? "justify-between" : "justify-end",
        )}
      >
        {showCheckbox && checkboxLabel && onCheckboxChange ? (
          <Checkbox
            label={<div className="whitespace-nowrap">{checkboxLabel}</div>}
            checked={checkboxChecked}
            onChange={(e) => onCheckboxChange(e.target.checked)}
          />
        ) : null}

        <div className="flex gap-4 ml-auto">
          <Button buttonType="cancel" data-testid="modal-cancel" onClick={() => onCancel()}>
            Cancel
          </Button>
          <TooltipTrigger disabled={!confirmButtonDisabled || !confirmButtonTooltip}>
            <span className="inline-flex">
              <Button
                buttonType={confirmButtonType}
                type="button"
                data-testid="modal-save"
                onClick={() => onConfirm()}
                disabled={confirmButtonDisabled || confirmLoading}
              >
                <span className="whitespace-nowrap flex items-center gap-2">
                  {confirmLoading && <FontAwesomeIcon icon={faSpinner} className="animate-spin" />}
                  {confirmButtonText || (variant === "danger" ? "Delete" : "Remove")}
                </span>
              </Button>
            </span>
            <Tooltip>{confirmButtonTooltip ?? ""}</Tooltip>
          </TooltipTrigger>
        </div>
      </div>
    </div>
  );

  // Positioned next to menu with click away
  if (anchorPosition) {
    return <div className="fixed inset-0 z-[999999] pointer-events-none">{dialogContent}</div>;
  }

  // Default: Centered modal
  return (
    <div
      className="fixed inset-0 z-[999999] flex justify-center items-center bg-black/50 px-3 py-6 overflow-y-auto"
      onClick={confirmLoading ? undefined : onCancel}
    >
      {dialogContent}
    </div>
  );
}
